import { Box, Divider, Typography } from '@mui/material';
import lodash from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../../lib/AlertContext';
import { Logger } from '../../../logger';
import { OrganizationService } from '../../../services/organization/organization.service';
import {
    AssignOrganizationRoleToUserArgs,
    GetUsersInOrganization,
    RemoveOrganizationUserArgs,
} from '../../../services/organization/types';
import { UserRole } from '../../../services/user/types';
import { OrganizationId } from '../../../types/idFlavors';
import { UsersListItem } from './UsersListItem';

export const styles = {
    tableSection: {
        display: 'flex',
        paddingInline: '16px 16px',
        marginBottom: '8px',
    },
};

type UsersListProps = {
    organizationId: OrganizationId;
    isUserOwner: boolean;
    users: GetUsersInOrganization[];
    setUsersInOrg: (users: GetUsersInOrganization[]) => void;
    isCredentialCreated: boolean;
};

export const UsersList: React.FC<UsersListProps> = (props) => {
    const { isUserOwner, organizationId, users, setUsersInOrg, isCredentialCreated } = props;
    const { t } = useTranslation();
    const { showConfirmationToUser } = useAlert();

    const sorted = lodash.sortBy(users, ['firstName', 'lastName']);

    const [selectedUser, setSelectedUser] = useState<GetUsersInOrganization | null>(null);

    const updateUsersList = async (): Promise<void> => {
        const updatedUsers = await OrganizationService.getUsersInOrganization(organizationId);
        setUsersInOrg(updatedUsers);
    };

    const isCurrentUserOnlyOwner = (): boolean =>
        isUserOwner && users.filter((user) => user.role === UserRole.OWNER).length === 1;

    const handleChangeUserRole = async (): Promise<void> => {
        if (!selectedUser) {
            Logger.error('Error when attempting to update user role, selectedUser does not exist');
            return;
        }

        const body: AssignOrganizationRoleToUserArgs = {
            organizationId: organizationId,
            role: selectedUser.role === UserRole.DEVELOPER ? UserRole.OWNER : UserRole.DEVELOPER,
            userId: selectedUser?.id,
        };

        await OrganizationService.assignOrganizationRoleToUser(body);
        await updateUsersList();
    };

    const renderChangeUserRoleModal = (): void => {
        showConfirmationToUser({
            title: t('ORGANIZATION_DETAILS_PAGE.UPDATE_ROLE_MODAL_TITLE', {
                newRole: selectedUser?.role === UserRole.DEVELOPER ? 'Owner' : 'Developer',
            }),
            description: (
                <>
                    {selectedUser?.role === UserRole.DEVELOPER
                        ? t('ORGANIZATION_DETAILS_PAGE.UPDATE_ROLE_MODAL_OWNER_BODY', {
                              firstName: selectedUser?.firstName,
                              lastName: selectedUser?.lastName,
                          })
                        : t('ORGANIZATION_DETAILS_PAGE.UPDATE_ROLE_MODAL_DEV_BODY', {
                              firstName: selectedUser?.firstName,
                              lastName: selectedUser?.lastName,
                          })}
                    <br />
                    <br />
                    {t('ORGANIZATION_DETAILS_PAGE.UPDATE_ROLE_MODAL_QUESTION')}
                </>
            ),
            testId: 'change-user-role-modal',
            confirmText: t('COMMON.UPDATE'),
            cancelText: t('COMMON.CANCEL'),
            onConfirm: handleChangeUserRole,
            errorTitle: t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_ADD_USER_ROLE_TO_ORG_TITLE'),
            errorDescription: t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_ADD_USER_ROLE_TO_ORG_BODY', {
                role: selectedUser?.role === UserRole.DEVELOPER ? t('COMMON.OWNER') : t('COMMON.DEVELOPER'),
            }),
        });
    };

    const handleRemoveUser = async (): Promise<void> => {
        if (!selectedUser) {
            throw new Error('Selected user not found');
        }

        const body: RemoveOrganizationUserArgs = {
            organizationId: organizationId,
            userId: selectedUser.id,
        };

        await OrganizationService.removeOrganizationUser(body);
        await updateUsersList();
    };

    const renderRemoveUserModal = (): void => {
        showConfirmationToUser({
            title: t('ORGANIZATION_DETAILS_PAGE.REMOVE_USER_MENU_ITEM'),
            description: (
                <>
                    {t('ORGANIZATION_DETAILS_PAGE.REMOVE_USER_MODAL_BODY', {
                        firstName: selectedUser?.firstName,
                        lastName: selectedUser?.lastName,
                    })}
                </>
            ),
            confirmText: t('COMMON.REMOVE'),
            cancelText: t('COMMON.CANCEL'),
            testId: 'remove-user-modal',
            onConfirm: handleRemoveUser,
            errorTitle: t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_REMOVE_USER_TO_ORG_TITLE'),
            errorDescription: t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_REMOVE_USER_TO_ORG_BODY'),
        });
    };

    const getMenuItems = (
        role: UserRole
    ): Array<{ testid: string; text: string; onClick: () => void; disabled?: boolean }> =>
        !(isCurrentUserOnlyOwner() && role === UserRole.OWNER)
            ? [
                  {
                      testid: role === UserRole.DEVELOPER ? 'make-owner' : 'make-developer',
                      text:
                          role === UserRole.DEVELOPER
                              ? t('ORGANIZATION_DETAILS_PAGE.MAKE_USER_OWNER_MENU_ITEM')
                              : t('ORGANIZATION_DETAILS_PAGE.MAKE_USER_DEVELOPER_MENU_ITEM'),
                      onClick: (): void => renderChangeUserRoleModal(),
                  },
                  {
                      testid: 'remove-user',
                      text: t('ORGANIZATION_DETAILS_PAGE.REMOVE_USER_MENU_ITEM'),
                      onClick: (): void => renderRemoveUserModal(),
                  },
              ]
            : [
                  {
                      testid: 'only-owner-menu-item',
                      text: t('ORGANIZATION_DETAILS_PAGE.ONLY_OWNER_MENU_ITEM'),
                      onClick: (): void => {},
                      disabled: true,
                  },
              ];

    return (
        <Box data-testid="users-list">
            <Box sx={styles.tableSection}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {t('ORGANIZATION_DETAILS_PAGE.USER_NAME')}
                </Typography>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {t('ORGANIZATION_DETAILS_PAGE.USER_ROLE')}
                </Typography>
            </Box>
            <Divider />
            {sorted.map((user, index) => (
                <UsersListItem
                    key={index}
                    user={user}
                    itemLabel={user.role}
                    userMenuItems={getMenuItems(user.role)}
                    isUserOwner={isUserOwner}
                    showUserMenu={selectedUser === user}
                    setShowUserMenu={setSelectedUser}
                    isCredentialCreated={isCredentialCreated}
                />
            ))}
        </Box>
    );
};
